import { Controller } from "@hotwired/stimulus"
import { I18n } from "i18n-js"
import locales from "../locales.json"

export default class extends Controller {
  static targets = ["list", "progress", "activateBtn"]
  static values = { url: String, checks: Array }

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || "hu"
    this.runChecks()
  }

  async runChecks() {
    const checks = this.checksValue
    let passed = 0
    let total = checks.length

    this.updateProgress(0, total)
    this.disableActivate()

    for (let i = 0; i < checks.length; i++) {
      const key = checks[i]
      const item = this.listTarget.querySelector(`[data-check-key="${key}"]`)

      this.setItemLoading(item, key)

      try {
        const response = await fetch(this.urlValue, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ check: key })
        })
        const result = await response.json()

        if (result.status === 'ok') {
          passed++
          this.setItemSuccess(item, key)
        } else {
          this.setItemFail(item, key, result)
        }
      } catch (e) {
        this.setItemFail(item, key, { message: this.i18n.t("mailboxes.wizard.network_error") })
      }

      this.updateProgress(i + 1, total, passed)
    }

    if (passed === total) {
      this.enableActivate()
    }
  }

  restart() {
    const checks = this.checksValue
    checks.forEach(key => {
      const item = this.listTarget.querySelector(`[data-check-key="${key}"]`)
      this.setItemPending(item, key)
    })
    this.runChecks()
  }

  setItemPending(item, key) {
    const badge = item.querySelector('.badge')
    const iconArea = item.querySelector('.fw-bold')
    const messageArea = item.querySelector('small')

    iconArea.innerHTML = `<span class="spinner-border spinner-border-sm text-secondary" role="status"></span> ${this.checkLabel(key)}`
    messageArea.textContent = this.i18n.t("mailboxes.wizard.waiting")
    messageArea.className = 'text-muted'
    badge.textContent = '...'
    badge.className = 'badge bg-secondary rounded-pill'
  }

  setItemLoading(item, key) {
    const iconArea = item.querySelector('.fw-bold')
    const messageArea = item.querySelector('small')

    iconArea.innerHTML = `<span class="spinner-border spinner-border-sm text-primary" role="status"></span> ${this.checkLabel(key)}`
    messageArea.textContent = this.i18n.t("mailboxes.wizard.checking")
    messageArea.className = 'text-muted'
  }

  setItemSuccess(item, key) {
    const badge = item.querySelector('.badge')
    const iconArea = item.querySelector('.fw-bold')
    const messageArea = item.querySelector('small')

    iconArea.innerHTML = `<i class="bi bi-check-circle-fill text-success"></i> ${this.checkLabel(key)}`
    messageArea.textContent = ''
    messageArea.className = ''
    badge.textContent = 'OK'
    badge.className = 'badge bg-success rounded-pill'
  }

  setItemFail(item, key, result) {
    const badge = item.querySelector('.badge')
    const iconArea = item.querySelector('.fw-bold')
    const messageArea = item.querySelector('small')

    iconArea.innerHTML = `<i class="bi bi-x-circle-fill text-danger"></i> ${this.checkLabel(key)}`

    const isDkim = key === 'dkim'
    let msg = result.message || ''
    if (result.expected) msg += this.renderValueLine(this.i18n.t("mailboxes.wizard.expected"), result.expected, isDkim)
    if (result.actual)   msg += this.renderValueLine(this.i18n.t("mailboxes.wizard.actual"), result.actual, isDkim)
    if (isDkim && (this.needsSplit(result.expected) || this.needsSplit(result.actual))) {
      msg += this.renderSplitToggle()
    }

    messageArea.innerHTML = msg
    messageArea.className = 'text-danger'

    badge.textContent = this.i18n.t("mailboxes.wizard.error_badge")
    badge.className = 'badge bg-danger rounded-pill'
  }

  // DKIM TXT records over 255 chars must be split into quoted chunks for most
  // DNS providers. Mirrors the split/normal toggle used on the setup_instructions
  // wizard page (mailbox_wizard_controller#toggleSplit), but built at runtime here
  // since the value is only known after the AJAX check response comes back.
  needsSplit(value) {
    return !!value && value.length > 255
  }

  renderValueLine(label, value, allowSplit) {
    if (allowSplit && this.needsSplit(value)) {
      const chunks = value.match(/.{1,255}/g) || []
      const chunksHtml = chunks
        .map(chunk => `<div class="d-none" data-dkim-split-row><code class="user-select-all text-break">"${chunk}"</code></div>`)
        .join('')
      return `<br><strong>${label}:</strong> <code class="user-select-all text-break" data-dkim-normal>${value}</code>${chunksHtml}`
    }
    return `<br><strong>${label}:</strong> <code class="user-select-all text-break">${value}</code>`
  }

  renderSplitToggle() {
    return `<div class="form-check form-switch mt-1">
      <input class="form-check-input" type="checkbox" role="switch" data-action="change->mailbox-verify#toggleDkimSplit">
      <label class="form-check-label small text-muted"><i class="bi bi-scissors me-1"></i>${this.i18n.t("mailboxes.wizard.dkim_split_toggle")}</label>
    </div>`
  }

  toggleDkimSplit(event) {
    const isSplit = event.currentTarget.checked
    const container = event.currentTarget.closest('small')
    if (!container) return

    container.querySelectorAll('[data-dkim-normal]').forEach(el => el.classList.toggle('d-none', isSplit))
    container.querySelectorAll('[data-dkim-split-row]').forEach(el => el.classList.toggle('d-none', !isSplit))
  }

  updateProgress(done, total, passed) {
    const pct = Math.round((done / total) * 100)
    const bar = this.progressTarget

    bar.style.width = `${pct}%`
    bar.textContent = `${done}/${total}`

    if (done === total) {
      bar.className = passed === total
        ? 'progress-bar bg-success'
        : 'progress-bar bg-warning'
    } else {
      bar.className = 'progress-bar progress-bar-striped progress-bar-animated'
    }
  }

  enableActivate() {
    if (this.hasActivateBtnTarget) {
      const btn = this.activateBtnTarget
      btn.disabled = false
      btn.classList.remove('disabled')
    }
  }

  disableActivate() {
    if (this.hasActivateBtnTarget) {
      const btn = this.activateBtnTarget
      btn.disabled = true
      btn.classList.add('disabled')
    }
  }

  checkLabel(key) {
    return this.i18n.t(`mailboxes.wizard.checks.${key}`)
  }
}
